import React, { useMemo } from 'react'
import QuestionsBlock from '../../questions-block'
import {useAppSelector} from "~/hooks/redux";
import {priceService} from "~/store/price/price-service";

interface IQuestions {
  header: string,
  description: string | React.ReactNode
}

interface IDICT {
  [key: string]: string;
}

const DAYS_IN_MONTH = 30

const CURRENCY_DICT: IDICT = {
  RUB: 'рублей',
  UZS: 'сум',
  KZT: 'тенге',
}

const RECORDS_LINK_DICT: IDICT = {
  RU: 'https://onlinepbx.ru/call_recording',
  UZ: 'https://pbx.uz/call_recording',
  KZ: 'https://onlinepbx.kz/call_recording'
}

const ANALYTICS_LINK_DICT: IDICT = {
  RU: 'https://onlinepbx.ru/business_analytics',
  UZ: 'https://pbx.uz/business_analytics',
  KZ: 'https://onlinepbx.kz/business_analytics'
}

const QUALITY_LINK_DICT: IDICT = {
  RU: 'https://site.onlinepbx.ru/features/quality',
  UZ: 'https://site.onlinepbx.ru/features/quality',
  KZ: 'https://site.onlinepbx.ru/features/quality'
}

const CHATS_LINK_DICT: IDICT = {
  RU: 'https://site.onlinepbx.ru/features/chats',
  UZ: 'https://site.onlinepbx.ru/features/chats',
  KZ: 'https://site.onlinepbx.ru/features/chats'
}

const AUTOINFORMER_LINK_DICT: IDICT = {
  RU: 'https://onlinepbx.ru/autoinformer',
  UZ: 'https://pbx.uz/autoinformer',
  KZ: 'https://onlinepbx.kz/autoinformer'
}

const PRICE_LINK_DICT: IDICT = {
  RU: 'https://onlinepbx.ru/cost',
  UZ: 'https://pbx.uz/cost',
  KZ: 'https://onlinepbx.kz/cost'
}

export const Questions: React.FC = () => {
  return (
    <div className='onlinepbx__help__question'>
      <div className='onlinepbx__container'>
        <h2 className='onlinepbx__content__header__center'>
          Вопросы и ответы
        </h2>
        {QuestionsBlock(getQuestions())}
      </div>
    </div>
  )
}

const getQuestions = (): IQuestions[] => {
  const { country } = useAppSelector(state => state.countryReducer)
  const { data: services, isLoading, isError } = priceService.useGetPricesQuery('')

  const SERVICE_PRICES = useMemo(() => {
    if (isLoading || isError) return {}

    const fees = services || []

    return fees.reduce((acc: any, fee: any) => {
      const key = `${fee.type}${fee.package_size || ''}`
      if (!acc[key]) {
        const priceInSelectedCurrency = fee.cost_in_currency_per_day?.find((costPerDay: any) => costPerDay.currency === country.currency)?.cost || 0
        acc[key] = priceInSelectedCurrency / 100 * DAYS_IN_MONTH
      }
      return acc
    }, {})
  }, [isLoading, isError, services, country])


  return [{
    header: 'Сколько стоит onlinePBX?',
    description: <div className='onlinepbx__question__description'>
      {
        isError ?
          <div className='onlinepbx__question__description__line'>
            Не удалось загрузить цену. Узнать актуальную цену можно по <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={PRICE_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>ссылке</a>.
          </div> :
          <>
            <div className='onlinepbx__question__description__line'>
              Базовый тариф стоит {(SERVICE_PRICES['ACCESS_PLATFORM'] + SERVICE_PRICES['USER'])?.toLocaleString('ru-Ru')}&nbsp;{CURRENCY_DICT[country?.currency || 'RUB']} в месяц. Он включает в себя 1&nbsp;рабочее место, безлимитную <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={RECORDS_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>запись разговоров</a>, модули <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={ANALYTICS_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>Бизнес-аналитика</a> и <a className='onlinepbx__question__description__link  onlinepbx__question__description__link__no__margin' target='_blank' href={QUALITY_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>Оценка качества</a>. Базовый тариф можно расширить дополнительными услугами, например, подключить <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={CHATS_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>чаты</a> или <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={AUTOINFORMER_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>автоинформатор</a>. Подробнее с тарифами вы можете ознакомиться в разделе <a className='onlinepbx__question__description__link onlinepbx__question__description__link__no__margin' target='_blank' href={PRICE_LINK_DICT[country?.value || 'RU']} rel='noopener noreferrer'>Цена</a>.
            </div>
            <div className='onlinepbx__question__description__line'>
              После регистрации действует пробный период 7&nbsp;дней, когда все услуги бесплатны. Протестируйте сервис, качество связи и работу нашей техподдержки.
            </div>
            <div className='onlinepbx__question__description__line'>
              Обратите внимание: за минуты разговора вы платите оператору связи.
            </div>
          </>
      }
    </div>
  }, {
    header: 'С какими операторами вы работаете и кого мне выбрать?',
    description: <div className='onlinepbx__question__description'>
      Подключите существующий номер или приобретите новый у наших партнеров. У нас есть опыт работы с 200 операторами. Весь список в
      <a
        href='https://onlinepbx.evateam.ru/docs/docs/DOC-000047#operatory-svyazi'
        target='_blank'
        className='onlinepbx__question__description__link'
        rel='noopener noreferrer'
      >
        базе знаний
      </a>.
      После регистрации вы можете приобрести новый номер у нашего парнёра VoxImplant.
    </div>
  }, {
    header: 'Как подключить виртуальную АТС?',
    description: <div className='onlinepbx__question__description'>
      Подключите виртуальную АТС в три этапа: зарегистрируйте и настройте АТС, интегрируйте CRM-систему, подключите и настройте рабочие места — <span className='onlinepbx__question__description__wrap'>IP-телефоны</span> или гарнитуры. Удалённая поддержка настроит АТС за вас, чтобы сэкономить время — это займёт от 20 минут до нескольких часов. Либо настройте самостоятельно при помощи инструкции<a
        target='_blank'
        className='onlinepbx__question__description__link'
        href='https://onlinepbx.evateam.ru/docs/docs/CmfDocument:1bdc44d6-48aa-11ee-848f-00161e050156#bystryj-start-v-online-pbx'
        rel='noopener noreferrer'
    >Как начать работу</a>.
    </div>
  }, {
    header: 'Подойдёт ли моё оборудование?',
    description: <div className='onlinepbx__question__description'>
      <div className='onlinepbx__question__description__line'>
        Большинство IP-телефонов и гарнитур поддерживается нашим сервисом, полного списка нет.
      </div>
      <div className='onlinepbx__question__description__line'>
        Рекомендуемые модели мы описали в
        <a
          target='_blank'
          href='https://onlinepbx.evateam.ru/docs/docs/DOC-000061#oborudovanie'
          className='onlinepbx__question__description__link'
          rel='noopener noreferrer'
        >
          базе знаний
        </a>. Предупреждаем, что могут возникнуть сложности со следующим оборудованием: роутеры Zyxel, телефоны <span className='onlinepbx__question__description__wrap'>D-Link</span>, GSM-шлюзы, устройства Yota, 3G-модемы и VOIP-шлюзы (кроме Cisco). Для стабильного соединения рекомендуемый проводной интернет, так как по WiFi могут возникать искажения.
      </div>

    </div>
  }]
}

export default Questions
